import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { toast } from 'sonner'
import { formatMediumDate } from '@/utils/date'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface AccessRequestItem {
  request_id: string
  firm_name: string
  contact_name: string
  email: string
  state: string
  cpa_license_number: string | null
  role_requested: string
  products_requested: string | null
  status: string
  submitted_at: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  license_verified: boolean
  ciam_invitation_sent: boolean
  ciam_invitation_sent_at: string | null
}

interface AccessRequestsResponse {
  requests: AccessRequestItem[]
  count: number
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {status}
    </span>
  )
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return formatMediumDate(iso)
}

export function AccessRequestsPage() {
  const { data, loading, error, execute } = useApi<AccessRequestsResponse>()

  const load = useCallback(() => {
    execute(() => apiClient.get<AccessRequestsResponse>('/v1/admin/access-requests').then(r => r.data))
  }, [execute])

  useEffect(() => {
    load()
  }, [load])

  const [approveId, setApproveId] = useState<string | null>(null)
  const [rejectId, setRejectId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleApprove() {
    if (!approveId) return
    setSubmitting(true)
    try {
      const res = await apiClient.post<{ message?: string; invitation_sent?: boolean }>(`/v1/admin/access-requests/${approveId}/approve`)
      const message = res?.data?.message
      if (message) {
        if (res.data.invitation_sent === false) toast.error(message)
        else toast.success(message)
      }
      setApproveId(null)
      load()
    } catch {
      toast.error('Failed to approve request.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleReject() {
    if (!rejectId) return
    setSubmitting(true)
    try {
      await apiClient.post(`/v1/admin/access-requests/${rejectId}/reject`, {
        reason: rejectReason,
      })
      toast.success('Request rejected.')
      setRejectId(null)
      setRejectReason('')
      load()
    } catch {
      toast.error('Failed to reject request.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading access requests…</div>
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load requests.</div>

  const requests = data?.requests ?? []

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Access Requests</h1>
          <p className="text-sm text-gray-500 mt-1">{requests.length} total</p>
        </div>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      {requests.length === 0 ? (
        <p className="text-sm text-gray-500">No access requests yet.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>License #</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map(r => (
                <TableRow key={r.request_id}>
                  <TableCell className="text-sm whitespace-nowrap">
                    {fmtDate(r.submitted_at)}
                  </TableCell>
                  <TableCell className="font-medium text-sm">{r.firm_name}</TableCell>
                  <TableCell className="text-sm">{r.contact_name}</TableCell>
                  <TableCell className="text-sm text-gray-600">{r.email}</TableCell>
                  <TableCell className="text-sm capitalize">{r.role_requested}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {r.cpa_license_number ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">{r.state}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {r.products_requested ?? '—'}
                  </TableCell>
                  <TableCell>
                    {r.status === 'approved' && r.ciam_invitation_sent ? (
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Approved
                        </span>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Invite sent {fmtDate(r.ciam_invitation_sent_at)}
                        </div>
                      </div>
                    ) : r.status === 'rejected' ? (
                      <div>
                        {statusBadge(r.status)}
                        <div className="text-xs text-gray-500 mt-0.5">{fmtDate(r.reviewed_at)}</div>
                      </div>
                    ) : (
                      statusBadge(r.status)
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {r.cpa_license_number && (
                        <a
                          href="https://cpaverifiy.org"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm">
                            Verify License
                          </Button>
                        </a>
                      )}
                      {r.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => setApproveId(r.request_id)}>
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setRejectId(r.request_id)
                              setRejectReason('')
                            }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Approve dialog */}
      <Dialog open={!!approveId} onOpenChange={open => !open && setApproveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Access Request</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will create a user account and send a CIAM invitation email. Confirm?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveId(null)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={submitting}>
              {submitting ? 'Approving…' : 'Confirm Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectId} onOpenChange={open => !open && setRejectId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Access Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              A rejection email will be sent. You may include an optional reason.
            </p>
            <Textarea
              placeholder="Reason (optional)"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={submitting}
            >
              {submitting ? 'Rejecting…' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
